import { useQuery } from '@tanstack/react-query';
import { useContractQuery } from './useContractQuery';
import { useReadContracts } from './useContracts';

/* -------------------------------------------------------------- *
 *   Counts and aggregates — pool-level singletons.
 *   Rarely-changing scalars (config constants and global counters).
 * -------------------------------------------------------------- */

export function useRequestCount() {
  return useContractQuery<bigint>(['auditingPool', 'getRequestCount'], { refetchInterval: 20_000 });
}

export function useTotalWeiRaisedAuditing() {
  return useContractQuery<bigint>(['auditingPool', 'total_wei_raised']);
}

/**
 * Total wei that has flowed FROM the protocol TO labs as milestone claims.
 * Computed by iterating every request's milestone schedule and summing the
 * amounts of milestones whose state machine has progressed to Claimed (2)
 * or beyond (ProofSubmitted, Disputed, Approved — all mean "ETH already
 * disbursed to the lab"). Reuses getMilestoneSchedule(reqId) which returns
 * (names, amounts, statuses) in one call.
 *
 * Returns 0n while loading or if no requests exist.
 */
export function useTotalClaimedByLabs() {
  const contracts = useReadContracts();
  const { data: countBn } = useRequestCount();
  const N = countBn !== undefined ? Number(countBn) : 0;

  return useQuery<bigint, Error>({
    queryKey: ['metha', 'auditingPool', 'totalClaimedByLabs', N] as const,
    queryFn: async () => {
      if (N === 0) return 0n;
      const schedules = await Promise.all(
        Array.from({ length: N }, (_, i) =>
          contracts.auditingPool.getMilestoneSchedule(BigInt(i))
        )
      );
      let total = 0n;
      for (const sched of schedules) {
        // ethers v6 Result: accessible by index OR by named property.
        // Named access preferred because order is robust to ABI tweaks.
        // Fallback to indexed for older artifact builds.
        const amounts = (sched.amounts ?? sched[1]) as bigint[];
        const statuses = (sched.statuses ?? sched[2]) as bigint[];
        for (let j = 0; j < amounts.length; j++) {
          if (Number(statuses[j]) >= 2) total += amounts[j];
        }
      }
      return total;
    },
    enabled: N >= 0,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useRejectionDeadlineDays() {
  return useContractQuery<bigint>(['auditingPool', 'rejectionDeadlineDays']);
}

export function useDefaultRequestDeposit() {
  return useContractQuery<bigint>(['auditingPool', 'requestDeposit']);
}

export function useAuditorReward() {
  return useContractQuery<bigint>(['auditingPool', 'auditorReward']);
}

export function useMaxAuditorsPerRequest() {
  return useContractQuery<bigint>(['auditingPool', 'MAX_AUDITORS_PER_REQUEST']);
}

export function useMaxDeliverablesPerMilestone() {
  return useContractQuery<bigint>(['auditingPool', 'MAX_DELIVERABLES_PER_MILESTONE']);
}
